import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Share2, Plus, X, Copy, Check, Link2, Mail } from "lucide-react";

export default function ShareDialog({ briefing, setBriefing }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState(false);
  const emails = briefing.shared_with || [];
  const token = briefing.share_token;
  const link = token ? `${window.location.origin}/shared/${token}` : "";

  const persist = async (nextEmails) => {
    const { data } = await api.post(`/briefings/${briefing.id}/share`, { emails: nextEmails });
    setBriefing((b) => ({ ...b, shared_with: data.shared_with, share_token: data.share_token }));
  };

  const addEmail = async () => {
    const e = email.toLowerCase().trim();
    if (!e || !e.includes("@")) return toast.error("Email invalide");
    if (emails.includes(e)) return toast.error("Déjà partagé");
    await persist([...emails, e]);
    setEmail("");
    toast.success(`Partagé avec ${e}`);
  };
  const removeEmail = async (e) => {
    await persist(emails.filter((x) => x !== e));
  };
  const ensureLink = async () => {
    if (!token) await persist(emails);
  };
  const copy = async () => {
    await ensureLink();
    navigator.clipboard.writeText(link || `${window.location.origin}/shared/${briefing.share_token}`);
    setCopied(true);
    toast.success("Lien copié");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) ensureLink(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="share-button"
          className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-cyan-300">
          <Share2 className="mr-1.5 h-4 w-4" /> Partager
        </Button>
      </DialogTrigger>
      <DialogContent className="border-slate-700 bg-[#0D1527] text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Partager avec l'instructeur</DialogTitle></DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400">
              <Link2 className="h-3.5 w-3.5" /> Lien de partage (lecture seule)
            </Label>
            <div className="mt-1.5 flex gap-2">
              <Input readOnly value={link} data-testid="share-link-input"
                className="border-slate-700 bg-slate-900/60 font-mono-data text-xs text-slate-300" />
              <Button onClick={copy} data-testid="copy-link-button"
                className="shrink-0 bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">Toute personne avec ce lien peut consulter et commenter.</p>
          </div>

          <div>
            <Label className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400">
              <Mail className="h-3.5 w-3.5" /> Partager par email (comptes instructeurs)
            </Label>
            <div className="mt-1.5 flex gap-2">
              <Input value={email} onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addEmail()} placeholder="instructeur@exemple.fr"
                data-testid="share-email-input" className="border-slate-700 bg-slate-900/60 text-slate-100" />
              <Button onClick={addEmail} data-testid="add-share-email-button"
                className="shrink-0 bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {emails.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {emails.map((e) => (
                  <li key={e} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm text-slate-200">
                    {e}
                    <button onClick={() => removeEmail(e)} className="rounded p-1 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-[11px] text-slate-500">L'instructeur (compte du même email) verra ce briefing dans « Partagés avec moi ».</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
